import React, { useState, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentDollar, faSave } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import SelectSearch from 'react-select-search'
import countries from '../../countries.json'
import states from '../../states.json'
import AuthContext from '../../middlewares/AuthContext'
import '../../select-search-styles.css' 

const url = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface MarketingForm {
  name                  : string
  description           : string
  street_address        : string
  address_line_2        : string
  city                  : string
  zip_code              : string
  invoice_street_address: string
  invoice_address_line_2: string
  invoice_city          : string
  invoice_zip_code      : string
  invoice_state         : string
  invoice_country       : string
  invoice_email         : string
  price                 : number
}

const INITIAL: MarketingForm = {
  name                  : '',
  description           : '',
  street_address        : '',
  address_line_2        : '',
  city                  : '',
  zip_code              : '',
  invoice_street_address: '',
  invoice_address_line_2: '',
  invoice_city          : '',
  invoice_zip_code      : '',
  invoice_state         : '',
  invoice_country       : '',
  invoice_email         : '',
  price                 : 0,
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1rem' }}>
    {children}
  </p>
)

const CreateMarketing: React.FC = () => {
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t }          = useTranslation()

  const [loading, setLoading]       = useState(false)
  const [country, setCountry]       = useState('')
  const [availStates, setAvailStates] = useState<any[]>([])
  const [state, setState]           = useState('')
  const [countryErr, setCountryErr] = useState(false)
  const [stateErr, setStateErr]     = useState(false)

  const formSchema = Yup.object().shape({
    name                  : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    description           : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    street_address        : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    address_line_2        : Yup.string().max(255),
    zip_code              : Yup.string().matches(/^[0-9]+$/, 'Must be only digits').min(3).max(10).required(t('utilities.is_a_required_field')),
    invoice_street_address: Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    invoice_address_line_2: Yup.string().max(255),
    invoice_zip_code      : Yup.string().matches(/^[0-9]+$/, 'Must be only digits').min(3).max(10).required(t('utilities.is_a_required_field')),
    invoice_state         : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    invoice_country       : Yup.string().min(3).max(255).required(t('utilities.is_a_required_field')),
    invoice_email         : Yup.string().email().required(t('utilities.is_a_required_field')),
    price                 : Yup.number().typeError('Need a number').min(0).max(99).required(t('utilities.is_a_required_field')),
  })

  const handleCountryChange = (val: any) => {
    setCountry(val)
    setCountryErr(false)
    const fd = (countries as any[]).find(c => c.name === val)
    if (fd) {
      setState('')
      setAvailStates((states as any[]).filter(s => s.country_id === fd.id))
    }
  }

  const handleSubmit = async (values: MarketingForm, { resetForm }: { resetForm: () => void }) => {
    if (!country.trim()) { setCountryErr(true); return }
    setCountryErr(false)
    if (!state) { setStateErr(true); return }
    setStateErr(false)

    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.post<{ response: string }>(
        `${url}marketings`,
        { ...values, country, state },
        { headers }
      )
      Swal.fire({ icon: 'success', title: res.data.response })
      resetForm()
      setCountry('')
      setState('')
      setAvailStates([])
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="row justify-content-center">
      <div className="col-lg-11">

        {/* Header card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius   : 16,
          boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
          border         : '1px solid #f1f5f9',
          overflow       : 'hidden',
          marginBottom   : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, backgroundColor: `${BRAND}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={faCommentDollar} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
              {t('marketing.create.creation')}
            </p>
          </div>
        </div>

        <Formik initialValues={INITIAL} validationSchema={formSchema} onSubmit={handleSubmit}>
          <Form>
            <div className="row g-4">

              {/* Name / Description / Price */}
              <div className="col-12">
                <div style={{
                  backgroundColor: '#fff',
                  borderRadius   : 16,
                  boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
                  border         : '1px solid #f1f5f9',
                  overflow       : 'hidden',
                }}>
                  <div style={{ height: 3, backgroundColor: BRAND }} />
                  <div style={{ padding: '1.5rem' }}>
                    <div className="row g-3">
                      <div className="col-md-5">
                        <FieldLabel>{t('marketing.name')} *</FieldLabel>
                        <Field type="text" name="name" className="form-control form-control-sm" />
                        <ErrorMessage name="name" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="col-md-5">
                        <FieldLabel>{t('marketing.description')} *</FieldLabel>
                        <Field as="textarea" name="description" className="form-control form-control-sm" style={{ height: 70 }} />
                        <ErrorMessage name="description" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="col-md-2">
                        <FieldLabel>{t('marketing.price')} *</FieldLabel>
                        <Field type="text" name="price" className="form-control form-control-sm" />
                        <ErrorMessage name="price" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Physical Address */}
              <div className="col-md-6">
                <div style={{
                  backgroundColor: '#fff',
                  borderRadius   : 16,
                  boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
                  border         : '1px solid #f1f5f9',
                  overflow       : 'hidden',
                  height         : '100%',
                }}>
                  <div style={{ height: 3, backgroundColor: BRAND }} />
                  <div style={{ padding: '1.5rem' }}>
                    <SectionTitle>{t('marketing.address')}</SectionTitle>

                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.street_address')} *</FieldLabel>
                      <Field type="text" name="street_address" className="form-control form-control-sm" />
                      <ErrorMessage name="street_address" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.address_line')}</FieldLabel>
                      <Field type="text" name="address_line_2" className="form-control form-control-sm" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.city')} *</FieldLabel>
                      <Field type="text" name="city" className="form-control form-control-sm" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.zip')} *</FieldLabel>
                      <Field type="text" name="zip_code" className="form-control form-control-sm" />
                      <ErrorMessage name="zip_code" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.country')} *</FieldLabel>
                      <SelectSearch
                        options={countries as any}
                        value={country}
                        onChange={handleCountryChange}
                        // name="country"
                        placeholder={t('marketing.create.country')}
                        search
                      />
                      {countryErr && <p className="text-danger small fw-bold mt-1 mb-0">Select a country</p>}
                    </div>
                    {availStates.length > 0 && (
                      <div className="mb-3">
                        <FieldLabel>{t('marketing.create.state')} *</FieldLabel>
                        <SelectSearch
                          options={availStates}
                          value={state}
                          onChange={(val: any) => { setState(val); setStateErr(false) }}
                          // name="state"
                          placeholder={t('marketing.create.state')}
                          search
                          closeOnSelect
                        />
                        {stateErr && <p className="text-danger small fw-bold mt-1 mb-0">Select a state</p>}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Invoice Address */}
              <div className="col-md-6">
                <div style={{
                  backgroundColor: '#fff',
                  borderRadius   : 16,
                  boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
                  border         : '1px solid #f1f5f9',
                  overflow       : 'hidden',
                  height         : '100%',
                }}>
                  <div style={{ height: 3, backgroundColor: '#3b82f6' }} />
                  <div style={{ padding: '1.5rem' }}>
                    <SectionTitle>{t('marketing.create.street_address_inv')}</SectionTitle>

                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.street_address_inv')} *</FieldLabel>
                      <Field type="text" name="invoice_street_address" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_street_address" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.address_line_inv')}</FieldLabel>
                      <Field type="text" name="invoice_address_line_2" className="form-control form-control-sm" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.city_inv')} *</FieldLabel>
                      <Field type="text" name="invoice_city" className="form-control form-control-sm" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.zip_inv')} *</FieldLabel>
                      <Field type="text" name="invoice_zip_code" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_zip_code" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.state_inv')} *</FieldLabel>
                      <Field type="text" name="invoice_state" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_state" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.country_inv')} *</FieldLabel>
                      <Field type="text" name="invoice_country" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_country" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('marketing.create.email_inv')} *</FieldLabel>
                      <Field type="email" name="invoice_email" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_email" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="col-12">
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    display        : 'inline-flex',
                    alignItems     : 'center',
                    justifyContent : 'center',
                    gap            : '0.5rem',
                    width          : '100%',
                    padding        : '0.55rem 1.5rem',
                    borderRadius   : 9999,
                    backgroundColor: loading ? '#94a3b8' : BRAND,
                    color          : '#fff',
                    fontWeight     : 700,
                    fontSize       : '0.875rem',
                    border         : 'none',
                    cursor         : loading ? 'not-allowed' : 'pointer',
                    boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                    transition     : 'filter 0.15s, transform 0.15s',
                  }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                >
                  {loading
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    : <FontAwesomeIcon icon={faSave} />
                  }
                  <span>{t('marketing.create.create_marketing')}</span>
                </button>
              </div>

            </div>
          </Form>
        </Formik>

      </div>
    </div>
  )
}

export default CreateMarketing
